import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import {
  isWithin,
  monthGrid,
  monthOf,
  monthTitle,
  shiftDateByMonths,
  shiftDays,
  shiftMonth,
  weekdayNames,
} from './calendar-month.ts';
import styles from './Calendar.module.css';

export type CalendarPopupProps = {
  id: string;
  value: string;
  today: string;
  min?: string | undefined;
  max?: string | undefined;
  locale: string;
  place: CSSProperties;
  onPick: (date: string) => void;
  onClose: () => void;
};

const DAY_MOVES: Readonly<Record<string, (date: string) => string>> = {
  ArrowLeft: (date) => shiftDays(date, -1),
  ArrowRight: (date) => shiftDays(date, 1),
  ArrowUp: (date) => shiftDays(date, -7),
  ArrowDown: (date) => shiftDays(date, 7),
  PageUp: (date) => shiftDateByMonths(date, -1),
  PageDown: (date) => shiftDateByMonths(date, 1),
};

const RTL_MIRROR: Readonly<Record<string, string>> = { ArrowLeft: 'ArrowRight', ArrowRight: 'ArrowLeft' };

const capitalised = (text: string, locale: string): string =>
  text.charAt(0).toLocaleUpperCase(locale) + text.slice(1);

const relativeLabels = (locale: string) => {
  const relative = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  return {
    previous: capitalised(relative.format(-1, 'month'), locale),
    next: capitalised(relative.format(1, 'month'), locale),
    today: capitalised(relative.format(0, 'day'), locale),
  };
};

const fullDate = (date: string, locale: string): string =>
  new Intl.DateTimeFormat(locale, { dateStyle: 'full', timeZone: 'UTC' }).format(
    new Date(`${date}T00:00:00Z`),
  );

export const CalendarPopup = ({
  id,
  value,
  today,
  min,
  max,
  locale,
  place,
  onPick,
  onClose,
}: CalendarPopupProps) => {
  const grid = useRef<HTMLDivElement>(null);
  const [focused, setFocused] = useState(value === '' ? today : value);
  const [shown, setShown] = useState(() => monthOf(focused));
  const labels = relativeLabels(locale);

  useEffect(() => {
    grid.current?.querySelector<HTMLButtonElement>('[tabindex="0"]')?.focus();
  }, [focused]);

  const focusDate = (date: string) => {
    setFocused(date);
    setShown(monthOf(date));
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
      return;
    }
    const rtl = event.currentTarget.closest('[dir="rtl"]') !== null;
    const key = rtl ? (RTL_MIRROR[event.key] ?? event.key) : event.key;
    const move = DAY_MOVES[key];
    if (move === undefined) return;
    event.preventDefault();
    focusDate(move(focused));
  };

  return (
    <div
      id={id}
      role="dialog"
      aria-label={monthTitle(shown, locale)}
      className={styles['popup']}
      style={place}
    >
      <header className={styles['header']}>
        <button
          type="button"
          className={styles['nav']}
          aria-label={labels.previous}
          title={labels.previous}
          onClick={() => {
            setShown(shiftMonth(shown, -1));
          }}
        >
          <ChevronLeft size={16} aria-hidden="true" className={styles['navIcon']} />
        </button>
        <strong className={styles['title']} aria-live="polite">
          {monthTitle(shown, locale)}
        </strong>
        <button
          type="button"
          className={styles['nav']}
          aria-label={labels.next}
          title={labels.next}
          onClick={() => {
            setShown(shiftMonth(shown, 1));
          }}
        >
          <ChevronRight size={16} aria-hidden="true" className={styles['navIcon']} />
        </button>
      </header>
      <div
        ref={grid}
        role="grid"
        tabIndex={-1}
        aria-label={monthTitle(shown, locale)}
        className={styles['grid']}
        onKeyDown={onKeyDown}
      >
        <div role="row" className={styles['week']}>
          {weekdayNames(locale).map((name) => (
            <span key={name} role="columnheader" className={styles['weekday']}>
              {name}
            </span>
          ))}
        </div>
        {Array.from({ length: 6 }, (_, week) => (
          <div key={week} role="row" className={styles['week']}>
            {monthGrid(shown)
              .slice(week * 7, week * 7 + 7)
              .map((cell) => (
                <span key={cell.date} role="gridcell" aria-selected={cell.date === value}>
                  <button
                    type="button"
                    tabIndex={cell.date === focused ? 0 : -1}
                    className={styles['day']}
                    data-outside={!cell.inMonth}
                    data-today={cell.date === today}
                    data-selected={cell.date === value}
                    aria-label={fullDate(cell.date, locale)}
                    disabled={!isWithin(cell.date, min, max)}
                    onClick={() => {
                      onPick(cell.date);
                    }}
                  >
                    {cell.day}
                  </button>
                </span>
              ))}
          </div>
        ))}
      </div>
      <footer className={styles['footer']}>
        <button
          type="button"
          className={styles['today']}
          disabled={!isWithin(today, min, max)}
          onClick={() => {
            onPick(today);
          }}
        >
          {labels.today}
        </button>
      </footer>
    </div>
  );
};
